import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import FileSourceDropdown from '@/components/Dropdowns/FileSourceDropdown';
import { FILE_COMPONENT_ICON } from '@/components/ChatInput/fileParamUtils';

// ============================================================================
// VARIANT TOGGLE — change this single line to test different ghost card looks.
// Available variants:
//   'detail'  Original. Horizontal pill with icon badge + title + description.
//   'tile'    Vertical drop-zone square. Big centered icon, "Add" CTA, hint.
//   'chip'    Minimal one-line outlined chip. Tightest footprint, like a tag.
//   'banner'  Bold tinted accent card. Type-colored background, big affordance.
// ============================================================================
const GHOST_CARD_VARIANT = 'tile';

// Icon picked by `validation.file.fileType` (broad bucket). Used as a
// fallback when the more specific `ui.component` doesn't have a dedicated
// icon in `FILE_COMPONENT_ICON`.
const FILE_TYPE_ICON = {
  audio: 'waveform',
  image: 'image',
  video: 'film',
  document: 'file-lines',
  any: 'file',
};

// Human-readable noun used in CTA copy (e.g. "Add audio").
const FILE_TYPE_NOUN = {
  audio: 'audio',
  image: 'image',
  video: 'video',
  document: 'document',
  any: 'file',
};

// Variant-only accent palette for the 'banner' design. Subtle tint so it
// reads in both light and dark themes.
const FILE_TYPE_TINT = {
  audio: { bg: 'rgba(99,102,241,0.10)', fg: '#818CF8' }, // indigo
  image: { bg: 'rgba(168,85,247,0.10)', fg: '#C084FC' }, // purple
  video: { bg: 'rgba(34,197,94,0.10)',  fg: '#4ADE80' }, // green
  document: { bg: 'rgba(245,158,11,0.10)', fg: '#FBBF24' }, // amber
  any: { bg: 'rgba(148,163,184,0.10)', fg: '#94A3B8' }, // slate
};

const useDerivedParam = (param) => {
  const fileType = param?.validation?.file?.fileType || 'any';
  const component = param?.ui?.component;
  // Component-specific icon takes precedence (e.g. waveform over image),
  // then fileType-bucket icon, then a generic file glyph.
  const iconName = FILE_COMPONENT_ICON[component]
    || FILE_TYPE_ICON[fileType]
    || FILE_TYPE_ICON.any;
  const displayName = param?.ui?.displayName || param?.name || 'File';
  const description = param?.ui?.description || param?.description;
  const isRequired = !!param?.validation?.required;
  const noun = FILE_TYPE_NOUN[fileType] || FILE_TYPE_NOUN.any;
  const tint = FILE_TYPE_TINT[fileType] || FILE_TYPE_TINT.any;
  return { fileType, iconName, displayName, description, isRequired, noun, tint };
};

// ============================================================================
// Variant A: 'detail' — original horizontal card
// ============================================================================
const detailStyles = (colors) => StyleSheet.create({
  container: {
    height: 56,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: Platform.OS === 'web' ? 'dashed' : 'solid',
    borderColor: colors.border_color_secondary,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 12,
    minWidth: 220,
    maxWidth: 320,
    flexShrink: 1,
    ...Platform.select({
      web: { cursor: 'pointer', transition: 'all 0.15s ease' },
    }),
  },
  containerHovered: {
    borderColor: colors.border_color_primary,
    backgroundColor: colors.solid_color_block,
  },
  containerRequired: { borderColor: colors.color_destructive },
  iconBadge: {
    width: 38,
    height: 38,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_block,
  },
  textColumn: { flex: 1, flexDirection: 'column', gap: 2, overflow: 'hidden' },
  title: { color: colors.text_color_primary, fontSize: 14, fontWeight: '500' },
  description: { color: colors.text_color_secondary, fontSize: 12, lineHeight: 15 },
  requiredAsterisk: { color: colors.color_destructive, fontSize: 14, fontWeight: '500' },
  chevron: { opacity: 0.6 },
});

const DetailVariant = ({ colors, isHovered, derived }) => {
  const styles = useMemo(() => detailStyles(colors), [colors]);
  const { iconName, displayName, description, isRequired } = derived;
  return (
    <View
      style={[
        styles.container,
        isHovered && styles.containerHovered,
        isRequired && styles.containerRequired,
      ]}
    >
      <View style={styles.iconBadge}>
        <Icon name={iconName} size={18} color={colors.text_color_secondary} />
      </View>
      <View style={styles.textColumn}>
        <Text style={styles.title} numberOfLines={1}>
          {displayName}
          {isRequired ? <Text style={styles.requiredAsterisk}> *</Text> : null}
        </Text>
        {!!description && (
          <Text style={styles.description} numberOfLines={2}>{description}</Text>
        )}
      </View>
      <Icon name="chevron-down" size={12} color={colors.text_color_secondary} style={styles.chevron} />
    </View>
  );
};

// ============================================================================
// Variant B: 'tile' — square drop-zone tile, vertical stack
// ============================================================================
const tileStyles = (colors) => StyleSheet.create({
  container: {
    width: 140,
    height: 140,
    minHeight: 140,
    paddingHorizontal: 10,
    paddingTop: 12,
    paddingBottom: 12,
    borderWidth: 1.5,
    borderStyle: Platform.OS === 'web' ? 'dashed' : 'solid',
    borderColor: colors.border_color_secondary,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 5,
    backgroundColor: 'transparent',
    flexShrink: 0,
    ...Platform.select({
      web: { cursor: 'pointer', transition: 'all 0.15s ease' },
    }),
  },
  containerHovered: {
    borderColor: colors.border_color_primary,
    backgroundColor: colors.solid_color_block,
    transform: [{ translateY: -1 }],
  },
  containerRequired: { borderColor: colors.text_color_tertiary },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_secondary,
  },
  cta: {
    color: colors.text_color_primary,
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  requiredAsterisk: {
    color: colors.color_destructive,
    fontSize: 13,
    fontWeight: '600',
  },
  description: {
    color: colors.text_color_secondary,
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 14,
  },
});

const TileVariant = ({ colors, isHovered, derived }) => {
  const styles = useMemo(() => tileStyles(colors), [colors]);
  const { iconName, displayName, description, isRequired, noun } = derived;
  // Description is the param author's instructions — far more useful than
  // the bare displayName ("audio_file"). Fall back to displayName if no
  // description is provided so the card still has secondary context.
  const subtitle = description || displayName;
  return (
    <View
      style={[
        styles.container,
        isHovered && styles.containerHovered,
        isRequired && styles.containerRequired,
      ]}
    >
      <View style={styles.iconCircle}>
        <Icon name={iconName} size={14} color={colors.text_color_secondary} />
      </View>
      <View style={styles.ctaRow}>
        <Text style={styles.cta} numberOfLines={1}>
          {`Add ${noun}`}
        </Text>
        {isRequired && <Text style={styles.requiredAsterisk}>*</Text>}
      </View>
      {!!subtitle && (
        <Text style={styles.description} numberOfLines={4}>
          {subtitle}
        </Text>
      )}
    </View>
  );
};

const VARIANTS = {
  detail: DetailVariant,
  tile: TileVariant,
};

/**
 * Ghost placeholder card displayed in the chat input attachment row when a
 * model defines a file-typed custom parameter that has no file attached yet.
 * Tapping the card opens {@link FileSourceDropdown}, which lets the user pick
 * from their library or upload a new file.
 *
 * The visual treatment is selectable via the {@link GHOST_CARD_VARIANT}
 * constant at the top of this file — change that single string to A/B test
 * the different layouts.
 */
const FileParamGhostCard = ({ param }) => {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const derived = useDerivedParam(param);

  const Variant = VARIANTS[GHOST_CARD_VARIANT] || VARIANTS.detail;

  // The inner element must not capture the press on native — `FileSourceDropdown`
  // wraps children in its own Pressable to drive the iOS ActionSheet / Android
  // popup menu. On web we keep a Pressable so hover styling works; on native we
  // render a plain View so the outer Pressable's `onPress` fires reliably.
  if (Platform.OS !== 'web') {
    return (
      <FileSourceDropdown param={param}>
        <View>
          <Variant colors={colors} isHovered={false} derived={derived} />
        </View>
      </FileSourceDropdown>
    );
  }

  return (
    <FileSourceDropdown param={param}>
      <Pressable
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        style={{ display: 'flex' }}
      >
        <Variant colors={colors} isHovered={isHovered} derived={derived} />
      </Pressable>
    </FileSourceDropdown>
  );
};

export default React.memo(FileParamGhostCard);
